import { renderTelephoneNumber } from '../../../utils/utils';
import { Tooltip } from '../../../components/v4';
import config from '../../../config.json';

const customAttributes = [
    {
        title: 'Custom Attribute 1',
        field: 'attribute1',
    },
    {
        title: 'Custom Attribute 2',
        field: 'attribute2',
    },
    {
        title: 'Custom Attribute 3',
        field: 'attribute3',
    },
];

export const sharedColumns = [
    {
        title: 'UPN',
        field: 'userPrincipalName',
    },
    {
        title: 'Telephone Number',
        field: 'telephoneNumber',
        render: (rowData) => renderTelephoneNumber(rowData, 'telephoneNumber'),
    },
    {
        title: 'Dial Plan',
        field: 'dialPlan',
    },
    {
        title: 'Voice Policy',
        field: 'voicePolicy',
    },
    {
        title: 'Managed By',
        field: 'managedBy',
        lookup: config.users.managedByJSON,
    },
    {
        title: 'Plan',
        field: 'plan',
    },
];

export const succeedAPIColumns = [
    {
        title: 'Display Name',
        field: 'displayName',
    },
    // {
    //     title: 'Status',
    //     field: 'status',

    // },
];

export const failedAPIColumns = [
    {
        title: 'NumberID',
        field: 'numberID',
    },
    {
        title: 'Failure Reasons',
        field: 'apiErrors',
    },
];

// default columns for the table
export const userTableColumns = [
    {
        title: 'Display Name',
        field: 'displayName',
        defaultSort: 'asc',
    },
    {
        title: 'Active',
        field: 'isActive',
        width: '5%',
        type: 'boolean',
    },
    ...sharedColumns,
    ...config.table.custom,
    {
        title: 'Usage Location',
        field: 'usageLocation',
    },
];

export const passedColumns = [
    {
        title: 'Display Name',
        field: 'displayName',
    },
    ...sharedColumns,
    ...customAttributes,
    {
        title: 'Verification Status',
        field: 'verificationStatus',
    },
];

export const failedColumns = [
    {
        title: 'Display Name',
        field: 'displayName',
    },
    ...sharedColumns,
    ...customAttributes,
    {
        title: 'Failure reasons',
        field: 'errors',
        width: '40%',
        render: (rowData) => {
            if (Array.isArray(rowData.errors) && rowData.errors.length > 0) {
                const erorString = rowData.errors.join('\n');
                return (
                    <span style={{ whiteSpace: 'pre-line' }}>
                        {' '}
                        {erorString}{' '}
                    </span>
                );
            }
            return null;
        },
    },
].map((v) => ({
    ...v,
    defaultSort: v.field === 'userPrincipalName' ? 'asc' : undefined,
}));
const upn = {
    title: 'UPN',
    field: 'userPrincipalName',
};
const telephoneNumber = {
    title: 'Telephone Number',
    field: 'telephoneNumber',
    cellRenderer: (params) =>
        renderTelephoneNumber(params.data, 'telephoneNumber'),
};
const dialPlan = {
    title: 'Dial Plan',
    field: 'dialPlan',
};
const voicePolicy = {
    title: 'Voice Policy',
    field: 'voicePolicy',
};
const managedBy = {
    title: 'Managed By',
    field: 'managedBy',
    lookup: config.users.managedByJSON,
};
const accountType = {
    title: 'Account Type',
    field: 'accountType',
    cellRenderer: (params) => params.data?.accountType || 'User',
};

// Per-platform column sets for the platform users table. Add a new platform's
// entry here (keyed by its `platformName`) instead of extending a switch/ternary
// chain — anything not listed falls back to `default`.
const PLATFORM_USER_COLUMNS = {
    '8x8': [
        upn,
        accountType,
        telephoneNumber,
        {
            title: 'Licenses',
            field: 'licenses',
            cellRenderer: (params) => {
                const licenses = params.data?.licenses;
                if (Array.isArray(licenses) && licenses.length > 0) {
                    return licenses.join('; ');
                }
                if (typeof licenses === 'string' && licenses.length > 0) {
                    return licenses;
                }
                return '—';
            },
        },
        managedBy,
    ],
    Zoom: [upn, telephoneNumber],
    RingCentral: [upn, telephoneNumber, managedBy],
    DialPad: [upn, telephoneNumber, managedBy],
    ms_resource_account: [upn, telephoneNumber, voicePolicy, managedBy],
    ms_resource_account_gcch: [upn, telephoneNumber, voicePolicy, managedBy],
    default: [upn, telephoneNumber, dialPlan, voicePolicy, managedBy],
};
PLATFORM_USER_COLUMNS['8X8'] = PLATFORM_USER_COLUMNS['8x8'];

export const getUserColumns = (platformName) => {
    const platformSpecificColumns =
        PLATFORM_USER_COLUMNS[platformName] ?? PLATFORM_USER_COLUMNS.default;

    const passedColumns = [
        {
            title: 'Display Name',
            field: 'displayName',
        },
        ...platformSpecificColumns,
        ...customAttributes,
        {
            title: 'Verification Status',
            field: 'verificationStatus',
        },
    ];

    const failedColumns = [
        {
            title: 'Display Name',
            field: 'displayName',
        },
        ...platformSpecificColumns,
        ...customAttributes,
        {
            title: 'Failure reasons',
            field: 'errors',
            width: '40%',
            cellRenderer: (params) => {
                const errors = params.data?.errors;
                if (Array.isArray(errors) && errors.length > 0) {
                    const errorString = errors.join('\n');
                    return (
                        <Tooltip label={errorString} hasArrow placement="top">
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                    cursor: 'help',
                                }}>
                                {errorString}
                            </span>
                        </Tooltip>
                    );
                }
                return params.value ?? null;
            },
        },
    ].map((v) => ({
        ...v,
        defaultSort: v.field === 'userPrincipalName' ? 'asc' : undefined,
    }));

    return { passedColumns, failedColumns };
};
